"use client";

import SmartEmailPhoneInput from "@/components/auth/SmartEmailPhoneInput";
import MasterButton from "@/components/common/masterButton";
import useToast from "@/hooks/useToast";
import { useAuthStore, usePendingCircleInviteOwnerUserIdSelector } from "@/store/useAuthStore";
import { signUp } from "@/utils/api";
import { loginBg, logoSmall } from "@/utils/image";
import { GENDER_OPTIONS } from "@/utils/constants";
import { validationPatterns } from "@/utils/validations";
import { useMutation } from "@tanstack/react-query";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { isValidPhoneNumber } from "libphonenumber-js";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Form as BSForm, Container } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { FiEye, FiEyeOff } from "react-icons/fi";
import * as Yup from "yup";

export default function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [cShowPassword, setCShowPassword] = useState(false);
  const router = useRouter();
  const { showToast } = useToast();
  const pendingInviteOwnerId = usePendingCircleInviteOwnerUserIdSelector();
  const isInvited = !!pendingInviteOwnerId;
  
  // 18 years ago ki date calculate kar rahe hain
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate()
  );
  const { setEmail, setOtpFlow } = useAuthStore();

  const initialValues = {
    firstName: "",
    lastName: "",
    emailOrPhone: "",
    username: "",
    password: "",
    confirm_password: "",
    gender: "",
    lookingForGender: "",
  };

  const validationSchema = Yup.object({
    firstName: isInvited
      ? Yup.string().trim().required("First name is required")
      : Yup.string().notRequired(),
    lastName: isInvited
      ? Yup.string().trim().required("Last name is required")
      : Yup.string().notRequired(),
    emailOrPhone: Yup.string()
      .trim()
      .required("Email or phone number is required")
      .test(
        "email-or-phone",
        "Invalid email or phone number",
        function (value) {
          if (!value) return false;

          // Check if it's an email
          if (value.includes("@")) {
            return validationPatterns.email.test(value);
          }

          // Check if it's a valid phone number
          try {
            return isValidPhoneNumber(value);
          } catch {
            return false;
          }
        }
      ),

    password: Yup.string()
      .trim()
      .required("Please enter password.")
      .test(
        "min-length",
        "Password must be at least 8 characters.",
        (value) => !value || value.length >= 8
      )
      .test(
        "uppercase",
        "Password must contain at least one uppercase letter.",
        (value) => !value || /[A-Z]/.test(value)
      )
      .test(
        "number",
        "Password must contain at least one number.",
        (value) => !value || /[0-9]/.test(value)
      )
      .test(
        "special-char",
        "Password must contain at least one special character.",
        (value) => !value || /[!@#$%^&*]/.test(value)
      ),

    username: Yup.string()
      .trim()
      .min(3, "At least 3 characters")
      .max(30, "Maximum 30 characters")
      .matches(
        /^[a-zA-Z0-9]+$/,
        "Username must be one word with no spaces or special characters"
      )
      .required("Username is required"),

    confirm_password: Yup.string()
      .required("Please confirm your password.")
      .oneOf([Yup.ref("password")], "Passwords must match."),
    gender: isInvited
      ? Yup.string().notRequired()
      : Yup.string().required("Gender is required"),
    lookingForGender: isInvited
      ? Yup.string().notRequired()
      : Yup.string().required("Looking for is required"),
  });

  const signUpMutation = useMutation({
    mutationFn: ({ payload }) => {
      return signUp(payload);
    },
    onSuccess: (obj, variables) => {
      const email = obj?.data?.email;
      const phone = obj?.data?.phone;
      const statusCode = obj?.data?.statusCode;
      const { setFieldError, emailOrPhone } = variables;

      // Store either email or phone in Zustand for all success scenarios
      const identifier = email || phone || emailOrPhone;
      setEmail(identifier);

      // Handle different backend response scenarios
      switch (statusCode) {
        case "USER_CREATED":
          // New user created, redirect to OTP
          setOtpFlow("signup");
          showToast({
            message:
              obj?.message ||
              `Account created successfully! Please verify your ${
                email ? "email" : "phone"
              }.`,
            type: "success",
          });
          router.push("/otp");
          break;

        case "USER_EXISTS_UNVERIFIED":
          // Existing unverified user, send new OTP and redirect to verification
          setOtpFlow("signup");
          showToast({
            message:
              obj?.message || "We've sent a new OTP to verify your account.",
            type: "success",
          });
          router.push("/otp");
          break;

        case "USER_ALREADY_EXISTS":
          // User already verified, show error on email/phone field
          if (setFieldError) {
            setFieldError(
              "emailOrPhone",
              obj?.message ||
                "This account is already registered. Please log in."
            );
          }
          showToast({
            message: obj?.message || "Account already exists. Please log in.",
            type: "error",
          });
          // Don't redirect, stay on signup page and show error
          break;

        default:
          // Fallback for any other success response
          setOtpFlow("signup");
          showToast({
            message: obj?.message || "Signup successful!",
            type: "success",
          });
          router.push("/otp");
      }
    }
  });

  const handleSubmit = (values, { setSubmitting, setFieldError }) => {
    const {
      firstName,
      lastName,
      emailOrPhone,
      username,
      password,
      gender,
      lookingForGender,
    } = values;

    // Base payload - only username and password
    const basePayload = {
      type: "signup",
      username,
      password,
      ...(isInvited
        ? { firstName, lastName, inviteCode: pendingInviteOwnerId }
        : { gender, lookingForGender }),
    };

    // Add email or phone
    const payload = emailOrPhone.includes("@")
      ? { ...basePayload, email: emailOrPhone }
      : { ...basePayload, phone: emailOrPhone };

    signUpMutation.mutate(
      { payload, setFieldError, emailOrPhone },
      {
        onSettled: () => setSubmitting(false),
      }
    );
  };

  return (
    <>
      <div className="position-relative commonAuthOuter ">
        <div className="d-flex justify-content-center m_bottom39">
          <Link href="/">
            <Image src={logoSmall} alt="logo" />
          </Link>
        </div>

        <Container
          fluid
          className="d-flex justify-content-center align-items-start"
        >
          <div
            className="login_Card"
            style={{ maxWidth: "883px", width: "100%" }}
          >
            <h3 className="text-center mb-3 heading24 black fw-bold">
              Create account
            </h3>
            <div className="age-warning mb-4">
              Must be at least 18 years old to use this app.
            </div>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting, values, setFieldValue, errors, touched }) => (
                <Form>
                  {/* Smart Email/Phone Input */}
                  <div className="row">
                    {isInvited && (
                      <>
                        <div className="col-md-6 mb-4">
                          <div className="position-relative">
                            <label className="customLabel">First Name</label>
                            <Field
                              as={BSForm.Control}
                              type="text"
                              name="firstName"
                              placeholder="Enter First Name"
                            />
                            <ErrorMessage
                              name="firstName"
                              component="div"
                              className="text-danger small mt-1"
                            />
                          </div>
                        </div>
                        <div className="col-md-6 mb-4">
                          <div className="position-relative">
                            <label className="customLabel">Last Name</label>
                            <Field
                              as={BSForm.Control}
                              type="text"
                              name="lastName"
                              placeholder="Enter Last Name"
                            />
                            <ErrorMessage
                              name="lastName"
                              component="div"
                              className="text-danger small mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}
                    <div className="col-md-6 mb-4">
                      <div className="position-relative">
                        <SmartEmailPhoneInput
                          value={values.emailOrPhone}
                          onChange={(value) =>
                            setFieldValue("emailOrPhone", value)
                          }
                          error={touched.emailOrPhone && errors.emailOrPhone}
                          placeholder="Email or phone no"
                          label="Email or Phone no"
                        />
                      </div>
                    </div>
                    <div className="col-md-6 mb-4">
                      <div className="position-relative">
                        <label className="customLabel">Username</label>
                        <Field
                          as={BSForm.Control}
                          type="text"
                          name="username"
                          placeholder="Enter Username"
                        />
                        <ErrorMessage
                          name="username"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div className="col-md-6">
                      <div className="mb-4">
                        <div className="position-relative">
                          <label className="customLabel">Password</label>
                          <Field
                            as={BSForm.Control}
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Password"
                            autoComplete="new-password"
                            className="icon_input"
                          />

                          <span
                            onClick={() => setShowPassword(!showPassword)}
                            className="password-toggle eye_icon"
                          >
                            {!showPassword ? (
                              <FiEyeOff className="gray" size={18} />
                            ) : (
                              <FiEye className="gray" size={18} />
                            )}
                          </span>
                        </div>
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>
                    {/* Confirm Password */}
                    <div className="col-md-6">
                      <div className="mb-4">
                        <div className="position-relative">
                          <label className="customLabel">
                            Confirm Password
                          </label>
                          <Field
                            as={BSForm.Control}
                            type={cShowPassword ? "text" : "password"}
                            name="confirm_password"
                            placeholder="Confirm Password"
                            autoComplete="new-password"
                            className="icon_input"
                          />
                          <span
                            onClick={() => setCShowPassword(!cShowPassword)}
                            className="password-toggle eye_icon"
                          >
                            {!cShowPassword ? (
                              <FiEyeOff className="gray" size={18} />
                            ) : (
                              <FiEye className="gray" size={18} />
                            )}
                          </span>
                        </div>
                        <ErrorMessage
                          name="confirm_password"
                          component="div"
                          className="text-danger small mt-1"
                        />
                      </div>
                    </div>

                    {!isInvited && (
                      <>
                        {/* Gender */}
                        <div className="col-md-6">
                          <div className="mb-4">
                            <div className="position-relative">
                              <label className="customLabel">Gender</label>
                              <Field
                                as={BSForm.Select}
                                name="gender"
                                className="form-control"
                              >
                                <option value="">Select</option>
                                {Object.entries(GENDER_OPTIONS)
                                  .filter(([key]) => key !== "BOTH")
                                  .map(([key, value]) => (
                                    <option key={key} value={key}>
                                      {value}
                                    </option>
                                  ))}
                              </Field>
                            </div>
                            <ErrorMessage
                              name="gender"
                              component="div"
                              className="text-danger small mt-1"
                            />
                          </div>
                        </div>

                        {/* Looking for */}
                        <div className="col-md-6">
                          <div className="mb-4">
                            <div className="position-relative">
                              <label className="customLabel">Looking for</label>
                              <Field
                                as={BSForm.Select}
                                name="lookingForGender"
                                className="form-control"
                              >
                                <option value="">Select</option>
                                {Object.entries(GENDER_OPTIONS).map(
                                  ([key, value]) => (
                                    <option key={key} value={key}>
                                      {value}
                                    </option>
                                  )
                                )}
                              </Field>
                            </div>
                            <ErrorMessage
                              name="lookingForGender"
                              component="div"
                              className="text-danger small mt-1"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Submit Button */}
                  <MasterButton
                    disabled={signUpMutation.isPending || isSubmitting}
                    buttonName={
                      signUpMutation.isPending ? "Signing Up..." : "Sign Up"
                    }
                    width={"w-100"}
                    type="submit"
                  />
                  {/* </div> */}
                </Form>
              )}
            </Formik>

            {/* Bottom Links */}
            <div className="text-center mt-4">
              <p className="mb-0 dontAcc">
                Already have an account?{" "}
                <Link href="/login" className="forgot_link bold">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </Container>

        {/* <Image
          className="login_bg"
          src={loginBg}
          alt="landing-bg"
          width={100}
          height={400}
        /> */}
      </div>
    </>
  );
}
